import type { RefObject } from "react";
import { Link } from "react-router-dom";

import type { PriceAnomalyRuleSnapshot } from "../../api/priceAnomalyRule";
import type { PriceVarianceFilters } from "../../api/reports";
import FilterDropdown from "../../components/FilterDropdown";
import type { MultiSelectOption } from "../../components/MultiSelectChips";
import { formatDateShort } from "../../lib/format";
import { ANALYSIS_PERIOD_LABELS, ANALYSIS_PERIODS, type AnalysisPeriod } from "./analysisPeriod";
import { MinDeltaPctControl } from "./MinDeltaPctControl";
import { monthLongLabel } from "./purchases";
import type { PriceVarianceView } from "./types";

const ALL = "__all__";
const SEVERAL = "__several__";
function singleChoice(values: readonly string[] | undefined): string {
  if (!values?.length) return ALL;
  return values.length === 1 ? values[0] : SEVERAL;
}

export function effectivePeriod(period: AnalysisPeriod | null, filters: PriceVarianceFilters): AnalysisPeriod {
  if (period) return period;
  return filters.from || filters.to ? "custom" : "all";
}

/**
 * Filter über den Reitern (Beta-Feedback 2026-10-07): Zeitraum, Abweichung,
 * Großhändler, Hersteller und „Weitere Filter“. Beim Einkaufsvolumen zählen
 * nur Zeitraum, Großhändler und Hersteller; die anderen treten zurück.
 */
export function AnalysisFilterBar({
  view,
  period,
  filters,
  rulePct,
  supplierOptions,
  manufacturerOptions,
  advancedCount,
  changed,
  filterButtonRef,
  drawerOpen,
  onPeriod,
  onDelta,
  onSupplier,
  onManufacturer,
  onOpenDrawer,
  onReset,
}: {
  view: PriceVarianceView;
  period: AnalysisPeriod;
  filters: PriceVarianceFilters;
  rulePct: number;
  supplierOptions: readonly MultiSelectOption[];
  manufacturerOptions: readonly MultiSelectOption[];
  advancedCount: number;
  changed: boolean;
  filterButtonRef: RefObject<HTMLButtonElement | null>;
  drawerOpen: boolean;
  onPeriod: (next: AnalysisPeriod) => void;
  onDelta: (next: number | null) => void;
  onSupplier: (next: string | null) => void;
  onManufacturer: (next: string | null) => void;
  onOpenDrawer: (opener: HTMLButtonElement) => void;
  onReset: () => void;
}) {
  const purchases = view === "purchases";
  const supplier = singleChoice(filters.supplier);
  const manufacturer = singleChoice(filters.manufacturer_id);
  const supplierChoices = [
    { value: ALL, label: "Alle" },
    ...(supplier === SEVERAL ? [{ value: SEVERAL, label: `${filters.supplier?.length} gewählt` }] : []),
    ...supplierOptions.map((option) => ({ value: option.key, label: option.label })),
  ];
  const manufacturerChoices = [
    { value: ALL, label: "Alle" },
    ...(manufacturer === SEVERAL ? [{ value: SEVERAL, label: `${filters.manufacturer_id?.length} gewählt` }] : []),
    ...manufacturerOptions.map((option) => ({ value: option.key, label: option.label })),
    // A chosen value stays visible while its option list is still loading.
    ...(manufacturer !== ALL && manufacturer !== SEVERAL && !manufacturerOptions.some((option) => option.key === manufacturer)
      ? [{ value: manufacturer, label: manufacturer }] : []),
  ];
  return (
    <section aria-label="Analyse filtern" className="mt-5 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <FilterDropdown<AnalysisPeriod>
            label="Zeitraum"
            ariaLabel="Zeitraum wählen"
            options={ANALYSIS_PERIODS.map((value) => ({ value, label: ANALYSIS_PERIOD_LABELS[value] }))}
            value={period}
            defaultValue="12m"
            showValue
            onChange={onPeriod}
          />
          {!purchases ? (
            <MinDeltaPctControl value={filters.min_delta_pct ?? null} onChange={onDelta} rulePct={rulePct} />
          ) : null}
          <FilterDropdown<string>
            label="Großhändler"
            ariaLabel="Großhändler wählen"
            options={supplierChoices}
            value={supplier}
            defaultValue={ALL}
            showValue
            onChange={(next) => next !== SEVERAL && onSupplier(next === ALL ? null : next)}
          />
          <FilterDropdown<string>
            label="Hersteller"
            ariaLabel="Hersteller wählen"
            options={manufacturerChoices}
            value={manufacturer}
            defaultValue={ALL}
            showValue
            onChange={(next) => next !== SEVERAL && onManufacturer(next === ALL ? null : next)}
          />
          {!purchases ? (
            <button
              ref={filterButtonRef}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              onClick={(event) => onOpenDrawer(event.currentTarget)}
              className={advancedCount > 0
                ? "inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 shadow-sm hover:bg-emerald-100"
                : "inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"}
            >
              Weitere Filter
              {advancedCount > 0 ? (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1.5 py-0.5 text-[11px] font-semibold text-white">{advancedCount}</span>
              ) : null}
            </button>
          ) : null}
        </div>
        {changed ? (
          <button type="button" onClick={onReset} className="h-9 rounded-md px-3 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            Filter zurücksetzen
          </button>
        ) : null}
      </div>
    </section>
  );
}

function periodPhrase(period: AnalysisPeriod, filters: PriceVarianceFilters, purchases: boolean): string {
  if (period === "all") return "aus dem gesamten Zeitraum";
  if (period === "custom") {
    if (filters.from && filters.to) return `vom ${formatDateShort(filters.from)} bis ${formatDateShort(filters.to)}`;
    if (filters.from) return `ab ${formatDateShort(filters.from)}`;
    if (filters.to) return `bis ${formatDateShort(filters.to)}`;
    return "aus dem gesamten Zeitraum";
  }
  // Rolling periods end today; "Letztes Jahr" has its own end.
  if (purchases && filters.from && period !== "last_year") return `von ${monthLongLabel(filters.from.slice(0, 7))} bis heute`;
  return {
    "12m": "der letzten 12 Monate",
    ytd: "dieses Jahres",
    last_year: "des letzten Jahres",
    "36m": "der letzten 3 Jahre",
  }[period];
}

function list(values: readonly string[] | undefined, options: readonly MultiSelectOption[]): string {
  return (values ?? []).map((value) => options.find((option) => option.key === value)?.label ?? value).join(", ");
}

/**
 * Ein Satz, der die gewählten Filter vorliest, und darunter aufklappbar, wie
 * gerechnet wird. Kein zusätzlicher Erklärkasten.
 */
export function AnalysisExplanation({
  view,
  period,
  filters,
  supplierOptions,
  manufacturerOptions,
  advancedCount,
  rule,
  canManageRule,
}: {
  view: PriceVarianceView;
  period: AnalysisPeriod;
  filters: PriceVarianceFilters;
  supplierOptions: readonly MultiSelectOption[];
  manufacturerOptions: readonly MultiSelectOption[];
  advancedCount: number;
  rule: PriceAnomalyRuleSnapshot;
  canManageRule: boolean;
}) {
  const purchases = view === "purchases";
  const suppliers = list(filters.supplier, supplierOptions);
  const manufacturers = list(filters.manufacturer_id, manufacturerOptions);
  const where = `${suppliers ? `bei ${suppliers}` : "bei allen Großhändlern"} ${manufacturers ? `für Artikel von ${manufacturers}` : "und Herstellern"}`;
  const threshold = filters.min_delta_pct != null ? ` – mindestens ${filters.min_delta_pct.toLocaleString("de-AT")} %` : "";
  return (
    <div className="mt-2 px-0.5 text-[13px] leading-5 text-slate-600" data-testid="analysis-explanation">
      <p>
        <span className="font-medium text-slate-700">Gezeigt:</span>{" "}
        {purchases ? (
          <>alle Einkäufe <strong className="font-semibold text-slate-800">{periodPhrase(period, filters, true)}</strong> nach Belegdatum, netto – {where}. Der laufende Monat zählt erst, wenn er abgeschlossen ist.</>
        ) : (
          <>Käufe <strong className="font-semibold text-slate-800">{periodPhrase(period, filters, false)}</strong>, die <strong className="font-semibold text-slate-800">teurer waren als der Vergleichspreis</strong> desselben Artikels{threshold} – {where}.</>
        )}
        {!purchases && advancedCount > 0 ? ` Dazu ${advancedCount === 1 ? "ein weiterer Filter" : `${advancedCount} weitere Filter`}.` : ""}
      </p>
      <details className="mt-1">
        <summary className="cursor-pointer font-medium text-emerald-700 hover:underline">So wird gerechnet</summary>
        <dl className="mt-2 grid max-w-3xl gap-x-5 gap-y-1.5 rounded-lg border border-slate-200 bg-white px-4 py-3 sm:grid-cols-[9rem_minmax(0,1fr)]">
          <dt className="font-semibold text-slate-800">Vergleichspreis</dt>
          <dd>Was ihr für denselben Artikel im Schnitt bezahlt habt – in den 12 Monaten vor dem Kauf, sonst 24 Monate, sonst alles davor.</dd>
          <dt className="font-semibold text-slate-800">Abweichung</dt>
          <dd>Wie viel Prozent der Preis über dem Vergleichspreis lag.</dd>
          <dt className="font-semibold text-slate-800">Mehrkosten</dt>
          <dd>Preisunterschied × Menge.</dd>
          <dt className="font-semibold text-slate-800">Einkaufsvolumen</dt>
          <dd>Nettobeträge der Rechnungspositionen nach Belegdatum.</dd>
          <dt className="font-semibold text-slate-800">Prüfregel</dt>
          <dd>
            Die Rechnungsprüfung meldet Käufe ab {Number(rule.threshold_pct).toLocaleString("de-AT")} %
            {rule.minimum_reference_count ? ` mit mindestens ${rule.minimum_reference_count} früheren Positionen` : ""}.
            {canManageRule ? <> <Link to="/einstellungen" className="font-medium text-emerald-700 hover:underline">In den Einstellungen ändern</Link></> : null}
          </dd>
        </dl>
      </details>
    </div>
  );
}
